import Controller, { OnBeforeFetch, OnClear, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import BaseController from '../../BaseController';
import {Inject} from '@odinjs/odin';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { AutocompleteAPI, FetchAPI, FilterAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import {DevicesListAliases, FragmentNames} from '../../../codegen';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import {ApplicationRoute} from '../../../constants/routes';
import {DeviceProps} from '../../../interfaces/devices';

import { HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Value } from '@philips-internal/oswald-ui-core';
import { ItemPepType, RulesItemConfiguration } from '../../../constants/utils-pep';
import { Endpoint } from '../../../constants/endpoints';
import DevicesService from '../commons/devices-service';
import { YesNoType } from '../../../utils/enum-utils';

interface FilterParam {
  isFilteringByInProgress: boolean;
  presentationType: TypePresentation;
  retroactiveDays: number;
}

interface TypePresentation {
  description: string,
  id: Presentation
}

enum Presentation {
  ALL_ENCOUNTERS = 'A',
  ALL_PERSON = 'P'
}

@Controller({component: DevicesListAliases.LAYOUT_DEVICES_LIST, fragment: FragmentNames.DEVICES_LIST})
class DevicesListController extends BaseController {

  @Inject(DevicesListAliases.FILTER_DEVICES)
  private filterDevicesAPI!: FilterAPI;

  @Inject(DevicesListAliases.PANEL_GLOBAL_ACTIONS)
  private panelDevicesAPI!: PanelAPI;

  internalNavigation!: boolean;

  @OnMount(DevicesListAliases.LAYOUT_DEVICES_LIST)
  onMount(): void {
    InformationBarUtils.collapsedBar = true;
    this.internalNavigation = false;

    const {encounterId, patientId} = this.historyService.getCurrentRouteParams();
    this.context.set({ encounterId: encounterId, patientId: patientId });

    this.checkItemEditPermission()
      .finally(() => {
        const allowInfo = InformationBarUtils.allowInfo && DevicesService.isAllowEdit;
        this.panelDevicesAPI.setVisible(allowInfo);
      });
  }

  @OnUnmount(DevicesListAliases.LAYOUT_DEVICES_LIST)
  onUnmount(): void {
    InformationBarUtils.collapsedBar = false;
  }

  @OnMount(DevicesListAliases.FILTER_DEVICES)
  onFilterMount(event: MountEvent): void {
    const record = {
      isFilteringByInProgress: true,
      presentationType: {
        description: MessageService.translate('label.all-encounter'),
        id: Presentation.ALL_ENCOUNTERS,
      },
      retroactiveDays: 15,
    };

    const filter = event.target as FilterAPI;
    const restored = filter.restoreState(true);
    if (restored) {
      Object.assign(record, filter.getRecord());
      filter.setData({
        ...record,
        presentationType: null
      });
    } else {
      filter.setData(this.getinitialFilterValue())
    }

    filter.setData(record);
    filter.submit();
  }

  getinitialFilterValue(): Record<string, Value> {
    const presentation =
    {
      description: MessageService.translate('label.all-encounter'),
      id: Presentation.ALL_ENCOUNTERS,
    };

    return {
      presentationType: presentation,
    }
  }

  @OnMount({ target: DevicesListAliases.AUTOCOMPLETE_TYPE_PRESENTATION})
  handleMountInstallationSuccess(event: MountEvent): void {
    const fieldApi = event.target as AutocompleteAPI;
    const data = [
      {
        description: MessageService.translate('label.all-encounter'),
        id: Presentation.ALL_ENCOUNTERS,
      },
      {
        description: MessageService.translate('label.all-patient'),
        id: Presentation.ALL_PERSON,
      }
    ]
    fieldApi.setData(data);
    fieldApi.setValue(data[0]);
  }

  @OnClear(DevicesListAliases.FILTER_DEVICES)
  onFilterClear(): void {
    this.filterDevicesAPI.clear();
  }

  @OnUnmount(DevicesListAliases.FILTER_DEVICES)
  OnFilterUnmount(e: MountEvent): void {
    const filter = e.target as FilterAPI;
    const record = filter.getRecord() as Record<string, FilterParam>;

    if (this.internalNavigation) {
      filter.setData(record);
      filter.persistState();
      return;
    }

    filter.clearPersistedState();
  }

  @OnClick(DevicesListAliases.DEVICES_CARD)
  onCardClick(event: ClickEvent): void {
    this.internalNavigation = true;

    const { deviceSequence, patientEncounterDeviceId } = event.value as DeviceProps;
    const patientId = this.context.get('patientId') as string;
    const encounterId = this.context.get('encounterId') as string;
    this.context.set({
      deviceSequence,
      encounterId,
      patientEncounterDeviceId,
      patientId
    });
    applicationContext.redirect(ApplicationRoute.DEVICES_VIEWER, {deviceSequence, patientEncounterDeviceId});
  }

  @OnBeforeFetch(DevicesListAliases.FETCH_DEVICES_LIST)
  onBeforeFetch(event: FetchEvent): void {
    const encounterId = this.context.get('encounterId') as string;
    const patientId = this.context.get('patientId') as string;
    const fetchApi = event.target as FetchAPI;
    const record = this.filterDevicesAPI.getRecord() as FilterParam;
    const isFilteringByInProgress = record.isFilteringByInProgress || false;
    const typePresentation = record.presentationType as unknown as TypePresentation;
    const isPresentationByEncounter = this.typePresentationIsByEncounter(typePresentation?.id ?? Presentation.ALL_ENCOUNTERS) || isFilteringByInProgress;
    const params = fetchApi.getParameters();
    fetchApi.setParameters({
      ...params,
      encounterCode: isPresentationByEncounter ? encounterId : '',
      personCode: isPresentationByEncounter ? '' : patientId,
      presentationType: null
    });
  }

  @OnClick(DevicesListAliases.ADD_DEVICES_CUSTOM_DYNAMIC_BUTTON)
  onClickAddedButton(): void {
    const { patientId, encounterId } = this.context.getBulkData() as Record<string, string>;
    applicationContext.redirect(ApplicationRoute.DEVICES_CREATE, { encounterId, patientId });
  }

  private typePresentationIsByEncounter(id: Presentation): boolean {
    return id.valueOf() === Presentation.ALL_ENCOUNTERS;
  }

  private checkItemEditPermission(): Promise<void> {
    const itemType = ItemPepType.DEVICES;
    return HttpService.get(Endpoint.PEP_CAN_EDIT_ITEM, { itemType })
      .then(response => {
        const configuration = response.body as RulesItemConfiguration;
        const isAllowEdit = configuration?.allowEditing === YesNoType.YES;
        DevicesService.isAllowEdit = isAllowEdit;
      })
  }
}

export default DevicesListController;
